import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import {
  reatomWalletSummary,
  summaryModelAtom,
} from '@root/entities/summary/atoms/reatom-wallets-summary'
import { Skeleton } from '@root/shared/ui-kit'

import { SummaryChart } from '../common/summary-chart'
import { SummaryChartChange } from '../common/summary-chart-change'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = ClassNameProps & {
  wallet: WalletModel | undefined
}

export const SummaryChartMiniature: React.FC<Props> = ({
  className,
  wallet,
}) => {
  const { t } = useTranslation()
  const [model] = useAtom((ctx) =>
    wallet ? reatomWalletSummary(wallet.id) : ctx.spy(summaryModelAtom),
  )
  const [chartData] = useAtom(model.chartData)
  const [priceChangeState] = useAtom(model.priceChangeState)

  return AsyncStateN.matchWaiting(priceChangeState, {
    Error: () => (
      <EmptyChart>
        <EmptyChartMessage>{t('common.loading-error')}</EmptyChartMessage>
        <EmptyChartFooter />
      </EmptyChart>
    ),
    Waiting: () => <Skeleton width={126} height={40} />,
    Success: () => {
      if (chartData === undefined) {
        return (
          <EmptyChart>
            <EmptyChartMessage>{t('common.no_data')}</EmptyChartMessage>
            <EmptyChartFooter />
          </EmptyChart>
        )
      }

      return (
        <Root className={className}>
          <SummaryChart
            data={chartData}
            changePercentage={priceChangeState.value?.percentage ?? 0}
          />
          <Bottom>
            <TimeFrame>{t('common.30d')}</TimeFrame>
            <Change
              change={priceChangeState.value?.absolute ?? BigNumber(0)}
              changePercentage={priceChangeState.value?.percentage ?? 0}
            />
          </Bottom>
        </Root>
      )
    },
  })
}

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
`
const Change = styled(SummaryChartChange)`
  font-size: 10px;
  font-weight: 600;
`

const TimeFrame = styled.div`
  color: var(--textA-7);
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`

const Bottom = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  padding-inline: 4px;
  align-items: center;
`

const EmptyChart = styled.div`
  color: var(--text-8);

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  display: grid;
  grid-template-rows: 1fr auto;
  align-content: center;

  height: 40px;
  width: 126px;
`

const EmptyChartMessage = styled.span`
  display: grid;
  align-content: center;
  justify-content: center;
  text-align: center;
`

const EmptyChartFooter = styled.div`
  height: 2px;
  background: repeating-linear-gradient(
    to right,
    var(--backgroundA-5) 0 2px,
    transparent 2px 4px
  );
`
